import React from 'react';
import {
  VStack,
  HStack,
  Text,
  View,
  Select,
  VisuallyHidden,
} from '@packages/systems/spring';
import {emotionStyled} from '@packages/utilities/styled';
// Deep import: the Frame package barrel also evaluates FrameSize, which reads
// FRAME_DIMENSION_MODE at module load. Partial Jest mocks of
// component-canvas/designer omit that enum and crash unrelated suites.
// eslint-disable-next-line webflow/module-top-level-imports -- see above
import {FrameFieldLabel} from '@packages/systems/plugins/Frame/FrameFieldLabel';
import {BaseBadge} from '../BaseBadge';
import {
  useDesignerSelector,
  useDispatch,
  type DesignerStores,
} from '@packages/systems/designer/flux';
import {toggleSettingsSection as toggleSettingsSectionAction} from '@packages/systems/designer/flux-action-creators';
import type {SettingsSectionFlags} from '@packages/systems/designer/external';
import type {BreakpointID} from '@packages/systems/style/types';
import {BREAKPOINT_ID_MAIN} from '@packages/systems/style/breakpoint-ids';
import {getBreakpointWording} from '@packages/systems/style/breakpoints-config';
import BreakpointsIcons from '@packages/systems/ui/shared-ui/BreakpointsIcons';
import {
  selectMCCSelectedFrame,
  selectMCCCustomViewportHeights,
  getMCCViewportHeight,
  mccFrameWidthChanged,
  mccFrameViewportHeightChanged,
} from '@packages/systems/multi-context-canvas/store';
import {
  FrameSizeUnitInput,
  SettingsSection,
  useFrameSizeTransition,
  getBreakpointWidthRange,
  getMinMaxFrameHeight,
  getFrameDOMElementById,
  useMeasuredFrameSize,
} from '@packages/systems/open-canvas/ui';

/**
 * Inlined to avoid an import cycle through
 * `designer-selectors → designer/open-canvas-config →
 * multi-context-canvas/designer (barrel) → MCCFrameSettings`.
 *
 * This is a trivial property selector; the original in
 * `@packages/systems/designer-selectors` remains there for other
 * consumers.
 */
export const selectBreakpoints = ({StyleBlockStore}: DesignerStores) =>
  StyleBlockStore.breakpoints;

const selectSettingsSectionFlags = (state: DesignerStores) =>
  state.ExternalStore.settingsSectionFlags;

const WIDTH_PRESET_OPTIONS = [{value: 'fixed', label: 'Fixed'}] as const;
const HEIGHT_PRESET_OPTIONS = [{value: 'fit', label: 'Fit'}] as const;

const DISABLED_SELECT_STYLE = {flex: 2, cursor: 'not-allowed'} as const;
// Always three children (label / middle / value) so the row has the same
// gap count as Width/Height and the PX column width stays stable. Middle
// stays empty — reset lives on the label via FrameFieldLabel (Frame Size /
// Styles pattern).
const VIEWPORT_HEIGHT_LABEL_STYLE = {
  flex: 2,
  whiteSpace: 'nowrap',
} as const;
const VIEWPORT_HEIGHT_MIDDLE_SLOT_STYLE = {flex: 1} as const;
const VIEWPORT_HEIGHT_VALUE_STYLE = {flex: 2, display: 'flex'} as const;
// Viewport height is a CSS unit base, not content-fit frame height — keep a
// non-zero floor so typing 0 cannot collapse vh lengths in the frame.
const MIN_VIEWPORT_HEIGHT = 1;

const VIEWPORT_HEIGHT_UNITS = ['vh', 'svh', 'dvh', 'lvh'] as const;

const MonoUnit = emotionStyled('span')({
  fontFamily: '$mono',
});

// One source for hover tooltip + aria description so the unit list can't
// drift. Hover copy is immutable — it does not change when the height is
// custom; reset copy lives only in the FrameFieldLabel menu.
const VIEWPORT_HEIGHT_DESCRIPTION = `Used for ${VIEWPORT_HEIGHT_UNITS.slice(0, -1).join(', ')}, and ${VIEWPORT_HEIGHT_UNITS.at(-1)} units on this breakpoint.`;

const VIEWPORT_HEIGHT_TOOLTIP = (
  <>
    Used for{' '}
    {VIEWPORT_HEIGHT_UNITS.map((unit, index) => (
      <React.Fragment key={unit}>
        {index > 0
          ? index === VIEWPORT_HEIGHT_UNITS.length - 1
            ? ', and '
            : ', '
          : null}
        <MonoUnit>{unit}</MonoUnit>
      </React.Fragment>
    ))}{' '}
    units
    <br />
    on this breakpoint.
  </>
);

const MCC_SIZE_KEY = 'mcc-size';

const MCCFrameSizeSection = ({
  frameId,
  breakpointId,
  frameWidth,
  breakpoints,
  expanded,
  onHeadingClick,
}: {
  frameId: string;
  breakpointId: BreakpointID;
  frameWidth: number | null;
  breakpoints: ReturnType<typeof selectBreakpoints>;
  expanded: boolean;
  onHeadingClick: () => void;
}) => {
  const dispatch = useDispatch();
  const frameSizeTransition = useFrameSizeTransition(frameId);
  const customViewportHeights = useDesignerSelector(
    selectMCCCustomViewportHeights
  );

  const transitionWidth = frameSizeTransition?.width;
  const transitionHeight = frameSizeTransition?.height;

  const displayWidth = transitionWidth ?? frameWidth ?? 0;

  const measuredSize = useMeasuredFrameSize({
    frameId,
    getFrameDOMElement: getFrameDOMElementById,
  });
  const displayHeight = transitionHeight ?? Math.round(measuredSize.height);

  const viewportHeight = getMCCViewportHeight(
    breakpointId,
    customViewportHeights
  );
  const isCustomViewportHeight = customViewportHeights?.[breakpointId] != null;
  const viewportHeightDescriptionId = React.useId();

  const {min: minWidth, max: maxWidth} = React.useMemo(
    () => getBreakpointWidthRange(breakpoints, breakpointId),
    [breakpoints, breakpointId]
  );
  const {minHeight, maxHeight} = getMinMaxFrameHeight();

  const handleWidthChange = React.useCallback(
    (value: number) => {
      const clamped = Math.max(minWidth, Math.min(maxWidth, value));
      dispatch(mccFrameWidthChanged({frameId, width: clamped}));
    },
    [dispatch, frameId, minWidth, maxWidth]
  );

  const handleViewportHeightChange = React.useCallback(
    (value: number) => {
      const clamped = Math.max(MIN_VIEWPORT_HEIGHT, Math.min(maxHeight, value));
      dispatch(mccFrameViewportHeightChanged({frameId, height: clamped}));
    },
    [dispatch, frameId, maxHeight]
  );

  const handleViewportHeightReset = React.useCallback(() => {
    dispatch(mccFrameViewportHeightChanged({frameId, height: null}));
  }, [dispatch, frameId]);

  return (
    <SettingsSection
      data-automation-id="mcc-frame-size-settings"
      aria-label="Size"
      expanded={expanded}
      onToggle={onHeadingClick}
      label="Size"
    >
      <VStack padding="$2" spacing={2}>
        <HStack align="center" spacing={1}>
          <Text
            colorVariant="secondary"
            style={{flex: 1}}
            data-automation-id="mcc-frame-size-width-label"
          >
            Width
          </Text>
          <Select
            data-automation-id="mcc-frame-size-width-preset"
            style={DISABLED_SELECT_STYLE}
            options={WIDTH_PRESET_OPTIONS}
            value="fixed"
            isDisabled
          />
          <FrameSizeUnitInput
            automationId="mcc-frame-size-width-input"
            inputValue={displayWidth}
            min={minWidth}
            max={maxWidth}
            onInputChange={handleWidthChange}
          />
        </HStack>
        <HStack align="center" spacing={1}>
          <Text
            colorVariant="secondary"
            style={{flex: 1}}
            data-automation-id="mcc-frame-size-height-label"
          >
            Height
          </Text>
          <Select
            data-automation-id="mcc-frame-size-height-preset"
            style={DISABLED_SELECT_STYLE}
            options={HEIGHT_PRESET_OPTIONS}
            value="fit"
            isDisabled
          />
          <FrameSizeUnitInput
            automationId="mcc-frame-size-height-input"
            inputValue={displayHeight}
            min={minHeight}
            max={maxHeight}
            disabled
            dimmed
          />
        </HStack>
        {viewportHeight != null ? (
          <HStack align="center" spacing={1}>
            <View style={VIEWPORT_HEIGHT_LABEL_STYLE}>
              <FrameFieldLabel
                label="Viewport height"
                inheritedFrom={null}
                automationId="mcc-frame-size-viewport-height-label"
                tooltip={VIEWPORT_HEIGHT_TOOLTIP}
                onReset={
                  isCustomViewportHeight ? handleViewportHeightReset : undefined
                }
              />
            </View>
            <View
              style={VIEWPORT_HEIGHT_MIDDLE_SLOT_STYLE}
              data-automation-id="mcc-frame-size-viewport-height-middle-slot"
            />
            <VisuallyHidden id={viewportHeightDescriptionId}>
              {VIEWPORT_HEIGHT_DESCRIPTION}
            </VisuallyHidden>
            <View
              style={VIEWPORT_HEIGHT_VALUE_STYLE}
              data-automation-id="mcc-frame-size-viewport-height-value"
            >
              <FrameSizeUnitInput
                automationId="mcc-frame-size-viewport-height-input"
                inputValue={viewportHeight}
                min={MIN_VIEWPORT_HEIGHT}
                max={maxHeight}
                aria-label="Viewport height"
                aria-describedby={viewportHeightDescriptionId}
                onInputChange={handleViewportHeightChange}
              />
            </View>
          </HStack>
        ) : null}
      </VStack>
    </SettingsSection>
  );
};

/**
 * Right-sidebar panel shown when a frame is selected on the Multi-Context Canvas.
 * Displays the current breakpoint and frame size controls.
 */
export const MCCFrameSettings = () => {
  const breakpoints = useDesignerSelector(selectBreakpoints);
  const selectedFrame = useDesignerSelector(selectMCCSelectedFrame);
  const settingsSectionFlags = useDesignerSelector(selectSettingsSectionFlags);
  const dispatch = useDispatch();
  const toggleSettingsSection = React.useCallback(
    (section: string) => {
      dispatch(toggleSettingsSectionAction({section}));
    },
    [dispatch]
  );

  if (!selectedFrame) {
    return null;
  }

  const sizeExpanded =
    settingsSectionFlags?.[MCC_SIZE_KEY as keyof SettingsSectionFlags] ?? true;

  const breakpointId = selectedFrame.breakpointId;
  const frameBreakpoint = breakpointId ? breakpoints[breakpointId] : null;

  const breakpointName = frameBreakpoint
    ? getBreakpointWording(frameBreakpoint.id).label
    : 'Desktop';
  const BreakpointIcon = frameBreakpoint
    ? BreakpointsIcons[frameBreakpoint.id]
    : BreakpointsIcons[BREAKPOINT_ID_MAIN];

  const isBaseBreakpoint = frameBreakpoint?.id === BREAKPOINT_ID_MAIN;

  const frameWidth = selectedFrame.width;

  return (
    <div data-automation-id="mcc-frame-settings-panel">
      <View data-automation-id="mcc-frame-header-section">
        <HStack
          align="center"
          justify="space-between"
          paddingX="$2"
          paddingTop="$3"
          paddingBottom="$3"
        >
          <Text font="title">Frame</Text>
        </HStack>
        <View width="100%" height="$0.25" borderBottom="$border1" />
      </View>

      <VStack padding="$2" spacing={2}>
        <HStack
          align="center"
          justify="space-between"
          data-automation-id="mcc-frame-settings-breakpoint-info"
        >
          <Text colorVariant="secondary">Breakpoint</Text>
          <HStack align="center" spacing={1} isFullWidth={false}>
            <View color="$textSecondary">
              <BreakpointIcon size="small" />
            </View>
            <Text
              colorVariant="primary"
              data-automation-id="mcc-frame-settings-breakpoint-name"
            >
              {breakpointName}
            </Text>
            {isBaseBreakpoint && <BaseBadge density="compact" />}
          </HStack>
        </HStack>
      </VStack>
      <View width="100%" height="$0.25" borderBottom="$border1" />
      <MCCFrameSizeSection
        frameId={selectedFrame.id}
        breakpointId={breakpointId}
        frameWidth={frameWidth}
        breakpoints={breakpoints}
        expanded={sizeExpanded}
        onHeadingClick={() => toggleSettingsSection(MCC_SIZE_KEY)}
      />
    </div>
  );
};
